import React from 'react';
import { TodayPuzzleModeSnapshot } from '../hooks/useAdminTodayPuzzle';

function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatCompletionRate(rate: number | null | undefined): string {
  if (rate == null) return '—';
  return `${Math.round(rate * 100)}%`;
}

function formatHintBreakdown(snapshot: TodayPuzzleModeSnapshot): string {
  const { hintBreakdown, wins } = snapshot;
  if (wins === 0 || hintBreakdown.zeroPct == null) return '—';
  return `${hintBreakdown.zeroPct}% · ${hintBreakdown.onePct}% · ${hintBreakdown.twoPct}% · ${hintBreakdown.threePct}%`;
}

function formatDate(dateStr: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr || '—';
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

interface TodayPuzzleTableProps {
  classic: TodayPuzzleModeSnapshot[];
  casual: TodayPuzzleModeSnapshot[];
}

function SnapshotRow({ snapshot }: { snapshot: TodayPuzzleModeSnapshot }) {
  return (
    <tr>
      <td>{formatDate(snapshot.date)}</td>
      <td>{snapshot.numSolutions ?? '—'}</td>
      <td>{snapshot.starts}</td>
      <td>{snapshot.wins}</td>
      <td>{formatCompletionRate(snapshot.completionRate)}</td>
      <td>{formatDuration(snapshot.medianElapsedTimeMs)}</td>
      <td>{formatHintBreakdown(snapshot)}</td>
    </tr>
  );
}

function ModeTable({ label, rows }: { label: string; rows: TodayPuzzleModeSnapshot[] }) {
  return (
    <div className="today-puzzle-table-wrap">
      <h3 className="today-puzzle-mode-title">{label}</h3>
      <table className="admin-data-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Num solutions</th>
            <th>Starts</th>
            <th>Wins</th>
            <th>Completion rate</th>
            <th>Median solve time</th>
            <th>
              Winners by hints (0/1/2/3)
              <span className="column-subtitle">Percentages among completers</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((snapshot) => (
            <SnapshotRow key={snapshot.date} snapshot={snapshot} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function TodayPuzzleTable({ classic, casual }: TodayPuzzleTableProps) {
  return (
    <div className="today-puzzle-tables">
      <ModeTable label="Classic" rows={classic} />
      <ModeTable label="Casual" rows={casual} />
    </div>
  );
}
